import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Swal from "sweetalert2";
import {
  deleteAnalysis,
  getHeaders,
  getStringValues,
  updateAnalysis,
} from "../../utils/computation/analysis";
import { getScatterPlot } from "../../utils/computation/getScatterPlot";
import { getColumnValues } from "../../utils/computation/getColumnValues";
import { getTTestResults } from "../../utils/computation/getTTestResults";
import { getTwoSampleTTestResults } from "../../utils/computation/getTwoSampleTTestResults";
import { getPairedTTestResults } from "../../utils/computation/getPairedTTestResults";
import { getBoxPlot } from "../../utils/computation/getBoxPlot";
import { getBoxPlotGroup } from "../../utils/computation/getBoxPlotGroup";
import { getHistogram } from "../../utils/computation/getHistogram";
import { getHistogramGroup } from "../../utils/computation/getHistogramGroup";
import { getTimeSeries } from "../../utils/computation/getTimeSeries";
import { getPareto } from "../../utils/computation/getPareto";
import { getDescriptiveStats } from "../../utils/computation/getDescriptiveStats";
import { getGraphicalSummary } from "../../utils/computation/getGraphicalSummary";
import { getCorrelation } from "../../utils/computation/getCorrelation";
import { getNormality } from "../../utils/computation/getNormality";
import { getANOVA } from "../../utils/computation/getANOVA";
import { getIndividualPlot } from "../../utils/computation/getIndividualPlot";
const GraphHeader = ({
  column,
  dateTime,
  title,
  firstColumn,
  secondColumn,
  analysisId,
  additionals,
  graphs,
}) => {
  const { documentId } = useParams();
  const token = useSelector((state) => state.auth.token);
  const data = useSelector((state) => state.dataTable.data);
  const location = useLocation();

  // check if location is in analysis page
  const isInAnalysisPage =
    location.pathname.split("/")[location.pathname.split("/").length - 1] ===
    "analysis";

  const handleDelete = async () => {
    // const {} = await Swal.fire("Warning", "", "question")
    try {
      await deleteAnalysis(token, documentId, analysisId);
    } catch (error) {
      return Swal.fire("Error", "There was an error in fetching", "error");
    }
    Swal.fire("Success", "Analysis deleted", "success");
  };

  const handleRerunFunction = async () => {
    const columnValuesArr = column
      .split(", ")
      .filter((col) => col !== "") // remove empty values
      .map((col) => getColumnValues(data, col));

    let header = column
      .split(", ")
      .filter((col) => col !== "") // remove empty values
      .map((col) => {
        const head = getHeaders(data, col);
        if (!head[0]) return "";
        return head[0];
      });
    let results;
    // return console.log("header", header);
    // if(!columnValuesArr.length) return;

    // good
    if (title === "Graphical Summary") {
      results = await getGraphicalSummary({
        value: columnValuesArr[0],
        confidenceLevel: additionals.confidenceLevel,
      });
    }
    // good
    if (title === "ANOVA (One-way)") {
      let [anovaResult, boxplotResult, ivpResult] = await Promise.all([
        getANOVA({ arr: columnValuesArr }),
        graphs.isBoxplot && getBoxPlotGroup(columnValuesArr, header),
        graphs.isIVP &&
          getIndividualPlot({
            value: columnValuesArr,
            header: header,
          }),
      ]);
      delete boxplotResult.inputs;
      results = { ...anovaResult, boxplotResult, ivpResult };
    }
    // good
    if (title === "Correlation") {
      let [corResult, scatterPlotResult] = await Promise.all([
        getCorrelation({
          arr1: columnValuesArr[0],
          arr2: columnValuesArr[1],
        }),
        graphs.isScatterplot &&
          getScatterPlot({
            arr1: columnValuesArr[0],
            arr2: columnValuesArr[1],
            withReg: false,
          }),
      ]);

      const [firstCorrCoef, secondCorrCoef] = corResult.correlation.corrcoef;
      corResult.correlation.corrcoef = { 0: firstCorrCoef, 1: secondCorrCoef };
      results = { ...corResult, ...scatterPlotResult };
    }
    // good
    if (title === "Descriptive Statistics") {
      results = await getDescriptiveStats(columnValuesArr[0]);
    }
    // good
    if (title === "Multi Variable Boxplot (ungroup)") {
      const { inputs, ...rest } = await getBoxPlotGroup(
        columnValuesArr,
        header
      );
      if (!inputs) return;

      results = {
        ...rest,
      };
    }
    // good
    if (title === "Individual Value Plot") {
      results = await getIndividualPlot({ value: columnValuesArr, header });
    }
    // good
    if (title === "Multi Variable Histogram") {
      const { selectedColors, isFit } = additionals;
      const { inputs, ...rest } = await getHistogramGroup(
        columnValuesArr,
        selectedColors,
        isFit
      );
      results = { ...rest };
    }
    // good
    if (title === "Normality Test") {
      results = await getNormality(columnValuesArr[0]);
    }
    // good
    if (title === "Paired t") {
      const pairedTTestResult = await getPairedTTestResults(
        columnValuesArr[0],
        columnValuesArr[1],
        additionals.confidenceLevel
      );
      let [ivpResult, boxplotResult, histogramResult] = await Promise.all([
        graphs.isIVP &&
          getIndividualPlot({
            value: [pairedTTestResult.container],
            header: ["Paired array"],
          }),
        graphs.isBoxplot &&
          getBoxPlotGroup([pairedTTestResult.container], ["Paired array"]),
        graphs.isHistogram && getHistogram([pairedTTestResult.container]),
      ]);
      delete boxplotResult.inputs;
      delete histogramResult.inputs;
      results = {
        ...pairedTTestResult,
        ivpResult,
        boxplotResult,
        histogramResult,
      };
    }
    // good
    if (title === "Pareto Chart") {
      const firstValue = getStringValues(data, column.split(", ")[0]);
      const secondValue = columnValuesArr[1];
      // const value = [...firstValue, ...secondValue];

      let paretoResult = await getPareto(secondValue, firstValue);
      // delete paretoResult.inputs;
      results = paretoResult;
    }
    // good
    if (title === "Simple Boxplot") {
      results = await getBoxPlot(columnValuesArr[0], header);
    }
    // good, confirm isFit property
    if (title === "Simple Histogram") {
      results = await getHistogram(columnValuesArr[0], additionals.isFit);
    }
    // good
    if (title === "Simple Scatterplot") {
      const { x, y, withReg } = additionals;
      const xvalue = getColumnValues(data, x);
      const yvalue = getColumnValues(data, y);
      results = await getScatterPlot({
        arr1: xvalue,
        arr2: yvalue,
        withReg,
      });
    }
    // good
    if (title === "Time Series") {
      const x = getStringValues(data, additionals.x);
      const y = getStringValues(data, additionals.y);
      results = await getTimeSeries(x, y);
    }
    // good
    if (title === "1-Sample t") {
      // console.log("columnValuesArr[0]", columnValuesArr[0]);
      const [tTestResult, boxPlotResult, histogramResult] = await Promise.all([
        getTTestResults(
          columnValuesArr[0],
          additionals.confidenceLevel * 100,
          additionals.meanHypothesis
        ),
        graphs.boxplot && getBoxPlot(columnValuesArr[0], [""]),
        graphs.histogram && getHistogram(columnValuesArr[0]),
      ]);
      results = { ...tTestResult, boxPlotResult, histogramResult };
      // return console.log("results", results);
    }
    // good
    if (title === "2-Sample t") {
      try {
        const [tTestResult, boxPlotResult, ivpResult] = await Promise.all([
          getTwoSampleTTestResults(
            columnValuesArr[0],
            columnValuesArr[1],
            additionals.confidenceLevel
          ),
          graphs.isBoxplot &&
            getBoxPlotGroup([columnValuesArr[0], columnValuesArr[1]], header),
          graphs.isIVP &&
            getIndividualPlot({
              value: [columnValuesArr[0], columnValuesArr[1]],
              header,
            }),
        ]);
        delete boxPlotResult.inputs;
        results = { ...tTestResult, boxPlotResult, ivpResult };
      } catch (err) {
        console.log(err);
      }
    }

    // console.log(token, documentId, analysisId, { ...results });
    try {
      await updateAnalysis(token, documentId, analysisId, { ...results });
    } catch (error) {
      return Swal.fire(
        "Sorry",
        `There was an error with rerunning the ${title}`,
        "error"
      );
    }
  };

  return (
    <>
      <h2>
        {title}
        {!isInAnalysisPage ? (
          <>
            <DeleteIcon
              style={{
                float: "right",
                color: "#777777",
                cursor: "pointer",
              }}
              onClick={handleDelete}
            />
            <Button
              variant="contained"
              style={{
                float: "right",
                width: "auto",
                height: "25px",
                marginRight: "10px",
              }}
              onClick={handleRerunFunction}
            >
              Rerun Function
            </Button>
          </>
        ) : null}
      </h2>

      <div>
        <h3
          style={{
            float: "left",
            color: "#777777",
            fontWeight: "normal",
          }}
        >
          Column {firstColumn || column}{" "}
          {secondColumn && " and Column " + secondColumn}
        </h3>
        <h4
          style={{
            float: "right",
            color: "#777777",
            fontWeight: "normal",
          }}
        >
          Run last {dateTime}
        </h4>
      </div>
    </>
  );
};

export default GraphHeader;
